import { useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'

import { Customer } from 'common-billing-server/types'
import { useBillingApi } from '../../api/billing/billing-context'
import { AutoComplete } from '../common/AutoComplete'

/** The purchase a transfer takes units from. */
export interface TransferSource {
    id: string
    name: string
    /** Unallocated units the purchase can still give away. */
    transferable: number
}

interface TransferLicensesDialogProps {
    /** The dialog is open while a source is set. */
    source: TransferSource | null
    customerId: string
    customerName: string
    onClose: () => void
    /** Performs the transfer and reports its own failures; resolves true when the dialog may close. */
    onTransfer: (source: TransferSource, recipient: Customer, quantity: number) => Promise<boolean>
}

/** Moves unallocated units of one purchase to another customer's inventory. */
export const TransferLicensesDialog = ({
    source,
    customerId,
    customerName,
    onClose,
    onTransfer,
}: TransferLicensesDialogProps) => {
    const billingApi = useBillingApi()
    const [recipient, setRecipient] = useState<Customer | null>(null)
    const [quantity, setQuantity] = useState(1)
    const [isTransferring, setIsTransferring] = useState(false)
    const maxQuantity = Math.max(source?.transferable ?? 1, 1)

    const close = () => {
        setRecipient(null)
        setQuantity(1)
        onClose()
    }

    const transfer = async () => {
        if (!source || !recipient) return
        setIsTransferring(true)
        try {
            if (await onTransfer(source, recipient, quantity)) close()
        } finally {
            setIsTransferring(false)
        }
    }

    return (
        <Dialog open={!!source} onClose={isTransferring ? undefined : close} maxWidth="sm" fullWidth>
            <DialogTitle>Transfer {source?.name}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Move unallocated units of this purchase from {customerName} to another customer. Both customers'
                    transaction histories record the transfer.
                </DialogContentText>
                <AutoComplete<Customer>
                    placeholder="Receiving customer"
                    value={recipient}
                    onValueSelected={setRecipient}
                    formatSelectedValue={(customer) => customer.name}
                    isOptionDisabled={(customer) => customer.id === customerId}
                    isClearable
                    isRequired
                    isDisabled={isTransferring}
                    api={(query) => billingApi.listCustomers({ ...query, filter: { searchName: query.filter } })}
                    dataTestId="transfer-recipient"
                />
                <TextField
                    fullWidth
                    type="number"
                    label="Quantity"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.min(maxQuantity, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                    margin="normal"
                    disabled={isTransferring}
                    helperText={`Up to ${maxQuantity} unallocated`}
                    slotProps={{ htmlInput: { min: 1, max: maxQuantity } }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={isTransferring}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={() => void transfer()}
                    disabled={!recipient || recipient.id === customerId || isTransferring}
                >
                    {isTransferring ? 'Transferring…' : 'Transfer'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
